import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function HealthNetworks() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Health'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8713" data-mp="ehspmopnpau2">
      <Header data-id="element-8714" data-mp="2u9ybswsx0hba" />
      <main className="pt-[120px]" data-id="element-8715" data-mp="2ua826lhvgqhi">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-8716" data-mp="2uc2r0aw9l2qv">
          <div className="max-w-3xl mx-auto" data-id="element-8717" data-mp="2z9b7m71rkyq0">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8718" data-mp="2z6yjtd9bru51">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-8719" data-mp="1b9brjigie6un">
                {isFR ? 'SECTEURS · SANTÉ' : 'SECTORS · HEALTH'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-8720" data-mp="1ka03m7mcdx4w">
                {isFR ? 'Identité vérifiée. Consentement. Accès.' : 'Verified identity. Consent. Access.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-8721" data-mp="1u8pp0adg5zpw">
                {isFR ? 'La santé se précipite vers l’IA. Pourtant, à peine 1 % des organisations jugent leur adoption pleinement mature — la plupart restent bloquées au stade des projets pilotes, freinées par des systèmes fragmentés.' : 'Health is rushing into AI. Yet barely 1% of organizations say their adoption is fully mature — most stay stuck in pilots, blocked by fragmented systems.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-8722" data-mp="1an44lfcqrak4">
                <Link to="/request-demo?industry=health" onClick={() => track('Demo CTA Clicked', {
                location: 'Health Hero',
                cta: 'Talk to us'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8723" data-mp="1em33ojbskd3n">
                  {isFR ? 'Parlez-nous' : 'Talk to us'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-8724" data-mp="1te3kzs667c1s" />
                </Link>
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Health Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8725" data-mp="1y90wd5qx9xhu">
                  {isFR ? 'Demander une démo' : 'Request a demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8726" data-mp="cy0zc9d7gffm" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8727" data-mp="l6t4reudssy">
          <div className="max-w-3xl mx-auto" data-id="element-8728" data-mp="3ru42wqihbf43">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-8729" data-mp="3murl4k6gsy8d">
              {isFR ? 'La recherche montre' : 'Research shows'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-base text-[#121212]/55 leading-relaxed flex items-start gap-3 mb-8" data-id="element-8730" data-mp="jdhrq92gr4l9">
              <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#121212]/30 shrink-0" aria-hidden="true" data-id="element-8731" data-mp="ad2zsquzur8s" />
              <span data-id="element-8732" data-mp="2z4nj2h8xd6jh">
                {isFR ? 'C’est pourquoi 61 % des organisations de santé qui adoptent l’IA passent par un partenaire spécialisé plutôt que de bâtir seules. ' : "That's why 61% of health organizations adopting AI go through a specialized partner rather than building alone. "}
                <a href="https://www.mckinsey.com/industries/healthcare/our-insights" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-8733" data-mp="rm40pxjl9mrr">
                  {isFR ? 'Voir la recherche' : 'See the research'}
                </a>
                .
              </span>
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-8734" data-mp="1akq11a7a79um">
              {isFR ? 'Contexte de marché : il ne s’agit pas de résultats de déploiement BCITI. Source : McKinsey, Technology Trends Outlook 2025.' : 'Market context. Not BCITI deployment results. Source: McKinsey, Technology Trends Outlook 2025.'}
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8735" data-mp="3dqvs461pjjhi">
          <div className="max-w-3xl mx-auto" data-id="element-8736" data-mp="38slvrs48ycub">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-8737" data-mp="38vqavkj92szr">
              {isFR ? 'En santé, l’IA échoue faute de fondation : qui accède à quoi, avec quel consentement. C’est exactement ce qu’offre notre fondation d’identité. La confiance se bâtit sur la vérification, pas sur une promesse.' : "In health, AI fails for lack of a foundation: who accesses what, with what consent. That's exactly what our identity foundation provides. Trust is built on verification, not on a promise."}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-8738" data-mp="2cb7qt79spco2" />
    </div>;
}